import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  DynamicReportFilter,
  DynamicReportAggregation,
  PivotFieldInfo,
  PivotRequest,
  PivotResponse,
  ReportTemplate,
} from "../types";
import {
  dynamicReportService,
  reportTemplateService,
} from "../services/dataService";
import { formatCurrency, formatNumber, turkishIncludes } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import "./DynamicReports.css";

type ChartType = "bar" | "line" | "pie";
type ViewMode = "table" | "chart" | "both";

const TEMPLATE_SOURCE = "UnifiedPivot";

const CHART_COLORS = [
  "#2563eb", "#0ea5e9", "#14b8a6", "#22c55e", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#0891b2", "#64748b",
  "#4f46e5", "#84cc16", "#f97316", "#06b6d4", "#a855f7",
];

const OPERATOR_LABELS: Record<string, string> = {
  eq: "Eşittir",
  neq: "Eşit Değil",
  gt: "Büyüktür",
  gte: "Büyük/Eşit",
  lt: "Küçüktür",
  lte: "Küçük/Eşit",
  contains: "İçerir",
  startswith: "İle Başlar",
  between: "Arasında",
  in: "Şunlardan biri",
};

const AGG_FUNCTIONS = [
  { value: "sum", label: "Toplam" },
  { value: "avg", label: "Ortalama" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maksimum" },
  { value: "count", label: "Adet" },
];

const CURRENCIES = [
  { code: "TRY", label: "₺ TL" },
  { code: "USD", label: "$ USD" },
  { code: "EUR", label: "€ EUR" },
];

// Fonksiyon seçimi anlamsız olan sayaç ölçüleri
const COUNT_ONLY_MEASURES = ["OrderCount", "CostRowCount"];

const MAX_ROWS = 3;
const MAX_COLS = 2;

const operatorsFor = (type: string): string[] =>
  type === "datetime"
    ? ["between", "eq", "gt", "gte", "lt", "lte"]
    : type === "bool"
      ? ["eq", "neq"]
      : ["decimal", "int", "float", "currency"].includes(type)
        ? ["eq", "neq", "gt", "gte", "lt", "lte", "between"]
        : ["in", "eq", "neq", "contains", "startswith"];

// ── Çoklu seçim (in operatörü, değer listesi destekli) ──
const MultiSelectChips: React.FC<{
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ options, selected, onChange }) => {
  const [search, setSearch] = useState("");
  const filtered = useMemo(
    () => (search ? options.filter((o) => turkishIncludes(o, search)) : options).slice(0, 300),
    [options, search],
  );
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <div className="pbi-multiselect">
      <input
        className="pbi-multiselect-search"
        placeholder={`Ara… (${selected.length} seçili)`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="pbi-multiselect-list">
        {filtered.length === 0 && <span className="pbi-hint">Değer yok</span>}
        {filtered.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`pbi-ms-chip ${selected.includes(opt) ? "active" : ""}`}
            onClick={() => toggle(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
};

const DynamicReports: React.FC = () => {
  // Alan modeli
  const [fields, setFields] = useState<PivotFieldInfo[]>([]);
  const [loadingFields, setLoadingFields] = useState(true);
  const [fieldSearch, setFieldSearch] = useState("");

  // Pivot yapılandırması — boş başlar, kullanıcı serbestçe kurar
  const [rows, setRows] = useState<string[]>([]);
  const [cols, setCols] = useState<string[]>([]);
  const [measures, setMeasures] = useState<DynamicReportAggregation[]>([]);
  const [filters, setFilters] = useState<DynamicReportFilter[]>([]);
  const [targetCurrency, setTargetCurrency] = useState("TRY");
  const [rateMode, setRateMode] = useState("historical");

  // Değer listeleri
  const [fieldValues, setFieldValues] = useState<Record<string, string[]>>({});

  // Sonuç
  const [result, setResult] = useState<PivotResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Görünüm
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [chartMeasureIdx, setChartMeasureIdx] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Şablonlar
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [templatePublic, setTemplatePublic] = useState(true);

  const [alertModal, setAlertModal] = useState<{ isOpen: boolean; message: string; type: "success" | "error" | "warning" | "info" }>(
    { isOpen: false, message: "", type: "info" });
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; message: string; type: "warning" | "danger"; onConfirm: () => void }>(
    { isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  // ── Yükleme ──
  useEffect(() => {
    (async () => {
      try {
        setLoadingFields(true);
        const [f, t] = await Promise.all([
          dynamicReportService.getPivotFields(),
          reportTemplateService.getAll().catch(() => []),
        ]);
        setFields(f);
        setTemplates((t as ReportTemplate[]).filter((x) => x.dataSource === TEMPLATE_SOURCE));
      } catch (e) {
        console.error("Pivot alanları yüklenemedi", e);
        setAlertModal({ isOpen: true, message: "Rapor alanları yüklenemedi. API bağlantısını kontrol edin.", type: "error" });
      } finally {
        setLoadingFields(false);
      }
    })();
  }, []);

  const fieldMap = useMemo(() => {
    const m: Record<string, PivotFieldInfo> = {};
    fields.forEach((f) => (m[f.fieldName] = f));
    return m;
  }, [fields]);

  const label = useCallback(
    (name: string) => fieldMap[name]?.displayName || result?.fieldLabels?.[name] || name,
    [fieldMap, result],
  );

  const dimensionFields = useMemo(() => fields.filter((f) => f.role === "dimension"), [fields]);
  const measureFields = useMemo(() => fields.filter((f) => f.role === "measure"), [fields]);
  const filterableFields = useMemo(
    () => fields.filter((f) => f.role === "dimension" || f.role === "filter"),
    [fields],
  );

  // Kategoriye göre gruplu boyutlar (arama filtresi uygulanmış)
  const groupedDimensions = useMemo(() => {
    const list = fieldSearch
      ? dimensionFields.filter((f) => turkishIncludes(f.displayName, fieldSearch))
      : dimensionFields;
    const groups: { category: string; items: PivotFieldInfo[] }[] = [];
    list.forEach((f) => {
      let g = groups.find((x) => x.category === f.category);
      if (!g) { g = { category: f.category, items: [] }; groups.push(g); }
      g.items.push(f);
    });
    return groups;
  }, [dimensionFields, fieldSearch]);

  const ensureFieldValues = useCallback(async (field: string) => {
    if (!field || fieldValues[field] !== undefined) return;
    setFieldValues((p) => ({ ...p, [field]: [] }));
    try {
      const vals = await dynamicReportService.getPivotFieldValues(field);
      setFieldValues((p) => ({ ...p, [field]: vals }));
    } catch {
      /* boş liste kalır */
    }
  }, [fieldValues]);

  useEffect(() => {
    filters.forEach((f) => {
      if (fieldMap[f.field]?.hasValueList && fieldValues[f.field] === undefined)
        ensureFieldValues(f.field);
    });
  }, [filters, fieldMap, fieldValues, ensureFieldValues]);

  // ── Bölge işlemleri ──
  const markDirty = () => setDirty(true);

  const addToRows = (f: string) => {
    if (rows.includes(f)) return;
    if (rows.length >= MAX_ROWS)
      return setAlertModal({ isOpen: true, message: `En fazla ${MAX_ROWS} satır alanı seçilebilir.`, type: "warning" });
    setCols((p) => p.filter((x) => x !== f));
    setRows((p) => [...p, f]);
    markDirty();
  };
  const addToCols = (f: string) => {
    if (cols.includes(f)) return;
    if (cols.length >= MAX_COLS)
      return setAlertModal({ isOpen: true, message: `En fazla ${MAX_COLS} sütun alanı seçilebilir.`, type: "warning" });
    setRows((p) => p.filter((x) => x !== f));
    setCols((p) => [...p, f]);
    markDirty();
  };
  const removeRow = (f: string) => { setRows((p) => p.filter((x) => x !== f)); markDirty(); };
  const removeCol = (f: string) => { setCols((p) => p.filter((x) => x !== f)); markDirty(); };
  const moveInList = (list: string[], setList: (v: string[]) => void, idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[idx], next[j]] = [next[j], next[idx]];
    setList(next);
    markDirty();
  };

  const addMeasure = (f: string) => {
    if (measures.some((m) => m.field === f)) return;
    setMeasures((p) => [...p, { field: f, function: COUNT_ONLY_MEASURES.includes(f) ? "sum" : "sum", alias: "" }]);
    markDirty();
  };
  const removeMeasure = (i: number) => { setMeasures((p) => p.filter((_, idx) => idx !== i)); markDirty(); };
  const setMeasureFn = (i: number, fn: string) => {
    setMeasures((p) => p.map((m, idx) => (idx === i ? { ...m, function: fn } : m)));
    markDirty();
  };

  const addFilter = (f?: string) => {
    const field = f || filterableFields[0]?.fieldName;
    if (!field) return;
    const ops = operatorsFor(fieldMap[field]?.dataType || "string");
    setFilters((p) => [...p, { field, operator: ops[0], value: "" }]);
    if (fieldMap[field]?.hasValueList) ensureFieldValues(field);
    markDirty();
  };
  const updateFilter = (i: number, u: Partial<DynamicReportFilter>) => {
    setFilters((p) => p.map((x, idx) => (idx === i ? { ...x, ...u } : x)));
    if (u.field && fieldMap[u.field]?.hasValueList) ensureFieldValues(u.field);
    markDirty();
  };
  const removeFilter = (i: number) => { setFilters((p) => p.filter((_, idx) => idx !== i)); markDirty(); };

  // ── Çalıştırma ──
  const buildRequest = (): PivotRequest => ({
    rows,
    columns: cols,
    measures,
    filters: filters.filter((f) => (f.value ?? "").toString().trim() !== ""),
    targetCurrency,
    rateMode,
  });

  const executeReport = useCallback(async (req?: PivotRequest) => {
    const request = req || buildRequest();
    if (request.rows.length === 0)
      return setAlertModal({ isOpen: true, message: "En az bir satır alanı seçin.", type: "warning" });
    if (request.measures.length === 0)
      return setAlertModal({ isOpen: true, message: "En az bir değer (ölçü) ekleyin.", type: "warning" });

    setLoading(true);
    try {
      const res = await dynamicReportService.executePivot(request);
      setResult(res);
      setDirty(false);
      setChartMeasureIdx(0);
    } catch (e: any) {
      setAlertModal({
        isOpen: true,
        message: typeof e?.response?.data === "string" ? e.response.data : "Pivot rapor çalıştırılırken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cols, measures, filters, targetCurrency, rateMode]);

  const handleExportExcel = async () => {
    if (!result) return;
    setExporting(true);
    try {
      const blob = await dynamicReportService.exportPivotExcel(buildRequest());
      downloadBlob(blob, `pivot_rapor_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      setAlertModal({ isOpen: true, message: "Excel export sırasında hata oluştu.", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  const downloadBlob = (blob: Blob, name: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click();
    window.URL.revokeObjectURL(url); document.body.removeChild(a);
  };

  // ── Şablonlar ──
  const loadTemplates = async () => {
    try {
      const t = await reportTemplateService.getAll();
      setTemplates((t as ReportTemplate[]).filter((x) => x.dataSource === TEMPLATE_SOURCE));
    } catch { /* sessiz */ }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim())
      return setAlertModal({ isOpen: true, message: "Şablon adı zorunludur.", type: "warning" });
    const config = { rows, cols, measures, filters, targetCurrency, rateMode };
    try {
      await reportTemplateService.create({
        name: templateName.trim(),
        description: templateDesc.trim() || undefined,
        dataSource: TEMPLATE_SOURCE,
        configJson: JSON.stringify(config),
        isPublic: templatePublic,
      });
      setAlertModal({ isOpen: true, message: "Şablon kaydedildi.", type: "success" });
      setShowSaveTemplate(false); setTemplateName(""); setTemplateDesc("");
      loadTemplates();
    } catch {
      setAlertModal({ isOpen: true, message: "Şablon kaydedilirken hata oluştu.", type: "error" });
    }
  };

  const handleLoadTemplate = (t: ReportTemplate, run: boolean) => {
    try {
      const c = JSON.parse(t.configJson);
      const nr = c.rows || []; const nc = c.cols || [];
      const nm = c.measures || []; const nf = c.filters || [];
      setRows(nr); setCols(nc); setMeasures(nm); setFilters(nf);
      setTargetCurrency(c.targetCurrency || "TRY");
      setRateMode(c.rateMode || "historical");
      setShowTemplates(false);
      if (run) {
        executeReport({
          rows: nr, columns: nc, measures: nm,
          filters: (nf as DynamicReportFilter[]).filter((f) => (f.value ?? "").toString().trim() !== ""),
          targetCurrency: c.targetCurrency || "TRY",
          rateMode: c.rateMode || "historical",
        });
      } else {
        setDirty(true);
      }
    } catch {
      setAlertModal({ isOpen: true, message: "Şablon yüklenirken hata oluştu.", type: "error" });
    }
  };

  const handleDeleteTemplate = (t: ReportTemplate) => {
    setConfirmModal({
      isOpen: true, type: "danger",
      message: `"${t.name}" şablonunu silmek istediğinize emin misiniz?`,
      onConfirm: async () => {
        try {
          await reportTemplateService.delete(t.reportTemplateId);
          loadTemplates();
        } catch {
          setAlertModal({ isOpen: true, message: "Şablon silinirken hata oluştu.", type: "error" });
        }
        setConfirmModal((p) => ({ ...p, isOpen: false }));
      },
    });
  };

  // ── Biçimleme ──
  const fmtMeasure = (v: number | null | undefined, dataType: string): string => {
    if (v == null) return "-";
    if (dataType === "currency") return formatCurrency(v, targetCurrency);
    if (dataType === "int") return formatNumber(v, 0);
    return formatNumber(v);
  };

  // ── Grafik verisi: satır toplamları × seçili ölçü ──
  const chartData = useMemo(() => {
    if (!result || result.rowHeaders.length === 0) return [];
    const mi = Math.min(chartMeasureIdx, result.measures.length - 1);
    return result.rowHeaders
      .map((h, r) => ({ name: h.join(" / "), value: Number(result.rowTotals[r]?.[mi] ?? 0) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 30);
  }, [result, chartMeasureIdx]);

  const hasCols = (result?.columnFields?.length ?? 0) > 0;

  if (loadingFields) return <PageLoader message="Rapor modeli yükleniyor..." />;

  return (
    <div className={`pbi-layout ${sidebarCollapsed ? "pbi-sidebar-collapsed" : ""}`}>
      {/* ─── SIDEBAR: ALANLAR ─── */}
      <aside className="pbi-sidebar">
        <div className="pbi-sidebar-head">
          {!sidebarCollapsed && <span className="pbi-sidebar-title">Alanlar</span>}
          <button className="pbi-sidebar-toggle" onClick={() => setSidebarCollapsed((p) => !p)} title={sidebarCollapsed ? "Genişlet" : "Daralt"}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {sidebarCollapsed ? <><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></>
                : <><polyline points="11 17 6 12 11 7" /><line x1="6" y1="12" x2="18" y2="12" /></>}
            </svg>
          </button>
        </div>

        {!sidebarCollapsed && (
          <div className="pbi-sidebar-body">
            {/* Para birimi */}
            <div className="pbi-side-section pbi-currency-section">
              <label className="pbi-side-label">Hedef Para Birimi</label>
              <div className="pbi-seg">
                {CURRENCIES.map((c) => (
                  <button key={c.code} className={targetCurrency === c.code ? "active" : ""}
                    onClick={() => { setTargetCurrency(c.code); markDirty(); }}>{c.label}</button>
                ))}
              </div>
              <label className="pbi-side-label" style={{ marginTop: 10 }}>Kur Modu</label>
              <div className="pbi-seg">
                <button className={rateMode === "historical" ? "active" : ""} onClick={() => { setRateMode("historical"); markDirty(); }} title="Kaydın kendi tarihindeki kur">O günkü kur</button>
                <button className={rateMode === "current" ? "active" : ""} onClick={() => { setRateMode("current"); markDirty(); }} title="En güncel TCMB kuru">Güncel kur</button>
              </div>
            </div>

            {/* Alan arama */}
            <div className="pbi-side-section">
              <input
                className="pbi-field-search"
                placeholder="Alan ara…"
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
              />
            </div>

            {/* Boyutlar — kategori grupları */}
            {groupedDimensions.map((g) => (
              <div key={g.category} className="pbi-side-section">
                <div className="pbi-cat-title">{g.category}</div>
                <div className="pbi-fieldlist">
                  {g.items.map((f) => {
                    const inRows = rows.includes(f.fieldName);
                    const inCols = cols.includes(f.fieldName);
                    return (
                      <div key={f.fieldName} className={`pbi-fielditem ${inRows || inCols ? "used" : ""}`}>
                        <span className="pbi-fielditem-name" title={f.displayName}>{f.displayName}</span>
                        <span className="pbi-fielditem-actions">
                          <button
                            className={`pbi-fa ${inRows ? "on" : ""}`}
                            title={inRows ? "Satırlardan kaldır" : "Satırlara ekle"}
                            onClick={() => (inRows ? removeRow(f.fieldName) : addToRows(f.fieldName))}
                          >S</button>
                          <button
                            className={`pbi-fa ${inCols ? "on" : ""}`}
                            title={inCols ? "Sütunlardan kaldır" : "Sütunlara ekle"}
                            onClick={() => (inCols ? removeCol(f.fieldName) : addToCols(f.fieldName))}
                          >Sü</button>
                          <button
                            className="pbi-fa"
                            title="Filtre ekle"
                            onClick={() => addFilter(f.fieldName)}
                          >F</button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Ölçüler */}
            <div className="pbi-side-section">
              <div className="pbi-cat-title">Değerler (Ölçüler)</div>
              <div className="pbi-fieldlist">
                {measureFields
                  .filter((f) => !fieldSearch || turkishIncludes(f.displayName, fieldSearch))
                  .map((f) => {
                    const used = measures.some((m) => m.field === f.fieldName);
                    return (
                      <div key={f.fieldName} className={`pbi-fielditem ${used ? "used" : ""}`}>
                        <span className="pbi-fielditem-name" title={f.displayName}>{f.displayName}</span>
                        <span className="pbi-fielditem-actions">
                          <button
                            className={`pbi-fa wide ${used ? "on" : ""}`}
                            title={used ? "Değerlerden kaldır" : "Değerlere ekle"}
                            onClick={() => {
                              const idx = measures.findIndex((m) => m.field === f.fieldName);
                              if (idx >= 0) removeMeasure(idx); else addMeasure(f.fieldName);
                            }}
                          >{used ? "✓" : "+"}</button>
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* ─── CANVAS ─── */}
      <main className="pbi-canvas">
        <div className="pbi-toolbar">
          <div className="pbi-toolbar-left">
            <h1 className="pbi-title">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="3" x2="9" y2="21" />
              </svg>
              Pivot Raporlar
            </h1>
            {result && (
              <span className="pbi-count-badge">
                {result.orderCount.toLocaleString("tr-TR")} sipariş · {result.recordCount.toLocaleString("tr-TR")} kayıt
              </span>
            )}
            {result && (
              <span className="pbi-rate-badge">{targetCurrency} · {rateMode === "historical" ? "O günkü kur" : "Güncel kur"}</span>
            )}
          </div>
          <div className="pbi-toolbar-right">
            <button className={`btn btn-primary ${dirty ? "pbi-btn-attention" : ""}`} onClick={() => executeReport()} disabled={loading}>
              {loading ? <span className="pbi-spinner" /> : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="5 3 19 12 5 21 5 3" /></svg>}
              Çalıştır
            </button>

            <div className="pbi-toggle-group">
              <button className={viewMode === "table" ? "active" : ""} onClick={() => setViewMode("table")} title="Tablo">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
              </button>
              <button className={viewMode === "chart" ? "active" : ""} onClick={() => setViewMode("chart")} title="Grafik">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
              </button>
              <button className={viewMode === "both" ? "active" : ""} onClick={() => setViewMode("both")} title="Her İkisi">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="18" height="7" /></svg>
              </button>
            </div>

            <button className="btn btn-success" onClick={handleExportExcel} disabled={exporting || !result || result.rowHeaders.length === 0}>
              {exporting ? <span className="pbi-spinner dark" /> : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>}
              Excel
            </button>
            <button className="btn btn-secondary" onClick={() => setShowSaveTemplate(true)}>Kaydet</button>
            <button className="btn btn-secondary" onClick={() => setShowTemplates(true)}>
              Şablonlar <span className="pbi-template-count">{templates.length}</span>
            </button>
          </div>
        </div>

        {/* ── PİVOT BÖLGELERİ ── */}
        <div className="pbi-zones">
          {/* Satırlar */}
          <div className="pbi-zone">
            <div className="pbi-zone-title">
              Satırlar <span className="pbi-zone-limit">{rows.length}/{MAX_ROWS}</span>
            </div>
            <div className="pbi-zone-chips">
              {rows.map((f, i) => (
                <span key={f} className="pbi-zone-chip">
                  {i > 0 && <button className="pbi-zc-move" onClick={() => moveInList(rows, setRows, i, -1)} title="Sola taşı">‹</button>}
                  {label(f)}
                  {i < rows.length - 1 && <button className="pbi-zc-move" onClick={() => moveInList(rows, setRows, i, 1)} title="Sağa taşı">›</button>}
                  <button className="pbi-zc-x" onClick={() => removeRow(f)} title="Kaldır">×</button>
                </span>
              ))}
              {rows.length === 0 && <span className="pbi-zone-empty">Soldan alan ekleyin (S)</span>}
            </div>
          </div>

          {/* Sütunlar */}
          <div className="pbi-zone">
            <div className="pbi-zone-title">
              Sütunlar <span className="pbi-zone-limit">{cols.length}/{MAX_COLS}</span>
            </div>
            <div className="pbi-zone-chips">
              {cols.map((f, i) => (
                <span key={f} className="pbi-zone-chip col">
                  {i > 0 && <button className="pbi-zc-move" onClick={() => moveInList(cols, setCols, i, -1)} title="Sola taşı">‹</button>}
                  {label(f)}
                  {i < cols.length - 1 && <button className="pbi-zc-move" onClick={() => moveInList(cols, setCols, i, 1)} title="Sağa taşı">›</button>}
                  <button className="pbi-zc-x" onClick={() => removeCol(f)} title="Kaldır">×</button>
                </span>
              ))}
              {cols.length === 0 && <span className="pbi-zone-empty">Opsiyonel (Sü)</span>}
            </div>
          </div>

          {/* Değerler */}
          <div className="pbi-zone pbi-zone-values">
            <div className="pbi-zone-title">Değerler</div>
            <div className="pbi-zone-chips">
              {measures.map((m, i) => (
                <span key={`${m.field}-${i}`} className="pbi-zone-chip value">
                  {label(m.field)}
                  {!COUNT_ONLY_MEASURES.includes(m.field) && (
                    <select
                      className="pbi-zc-fn"
                      value={m.function}
                      onChange={(e) => setMeasureFn(i, e.target.value)}
                    >
                      {AGG_FUNCTIONS.map((fn) => <option key={fn.value} value={fn.value}>{fn.label}</option>)}
                    </select>
                  )}
                  <button className="pbi-zc-x" onClick={() => removeMeasure(i)} title="Kaldır">×</button>
                </span>
              ))}
              {measures.length === 0 && <span className="pbi-zone-empty">Soldan ölçü ekleyin (+)</span>}
            </div>
          </div>
        </div>

        {/* ── FİLTRELER ── */}
        <div className="pbi-filterbar">
          <div className="pbi-filterbar-head">
            <span className="pbi-zone-title">Filtreler</span>
            <button className="pbi-link-btn" onClick={() => addFilter()}>+ Filtre Ekle</button>
          </div>
          {filters.length > 0 && (
            <div className="pbi-filterbar-rows">
              {filters.map((f, idx) => {
                const info = fieldMap[f.field];
                const type = info?.dataType || "string";
                const listVals = fieldValues[f.field] || [];
                return (
                  <div key={idx} className="pbi-filter-row">
                    <select value={f.field} onChange={(e) => {
                      const ops = operatorsFor(fieldMap[e.target.value]?.dataType || "string");
                      updateFilter(idx, { field: e.target.value, operator: ops[0], value: "", value2: undefined });
                    }}>
                      {filterableFields.map((c) => <option key={c.fieldName} value={c.fieldName}>{c.displayName}</option>)}
                    </select>
                    <select value={f.operator} onChange={(e) => updateFilter(idx, { operator: e.target.value })}>
                      {operatorsFor(type).map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
                    </select>

                    {type === "datetime" ? (
                      <input type="date" value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })} />
                    ) : type === "bool" ? (
                      <select value={f.value} onChange={(e) => updateFilter(idx, { value: e.target.value })}>
                        <option value="">Seçin</option><option value="true">Evet</option><option value="false">Hayır</option>
                      </select>
                    ) : info?.hasValueList && f.operator === "in" ? (
                      <MultiSelectChips
                        options={listVals}
                        selected={f.value ? f.value.split(",").filter(Boolean) : []}
                        onChange={(next) => updateFilter(idx, { value: next.join(",") })}
                      />
                    ) : info?.hasValueList ? (
                      <>
                        <input list={`pvl-${idx}`} value={f.value} placeholder="Seç veya yaz" onChange={(e) => updateFilter(idx, { value: e.target.value })} />
                        <datalist id={`pvl-${idx}`}>
                          {listVals.slice(0, 500).map((v) => <option key={v} value={v} />)}
                        </datalist>
                      </>
                    ) : (
                      <input
                        type={["decimal", "int", "float", "currency"].includes(type) ? "number" : "text"}
                        value={f.value}
                        placeholder="Değer"
                        onChange={(e) => updateFilter(idx, { value: e.target.value })}
                      />
                    )}

                    {f.operator === "between" && (
                      <input
                        type={type === "datetime" ? "date" : "number"}
                        value={f.value2 || ""}
                        placeholder="Üst değer"
                        onChange={(e) => updateFilter(idx, { value2: e.target.value })}
                      />
                    )}
                    <button className="pbi-btn-remove" onClick={() => removeFilter(idx)}>✕</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="pbi-canvas-body">
          {loading && !result && <PageLoader message="Rapor çalıştırılıyor..." />}

          {!result && !loading && (
            <div className="pbi-empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="3" x2="9" y2="21" />
              </svg>
              <h2>Pivot Rapor Oluşturun</h2>
              <p>Soldan alanları <strong>Satır (S)</strong>, <strong>Sütun (Sü)</strong> ve <strong>Değer (+)</strong> bölgelerine ekleyin, ardından <strong>Çalıştır</strong>'a tıklayın.</p>
            </div>
          )}

          {result && (
            <>
              {/* KPI: genel toplamlar */}
              {result.measures.length > 0 && (
                <div className="pbi-kpi-row">
                  {result.measures.slice(0, 6).map((m, i) => (
                    <div key={m.alias} className="pbi-kpi-card" style={{ borderLeftColor: CHART_COLORS[i % CHART_COLORS.length] }}>
                      <span className="pbi-kpi-value">{fmtMeasure(result.grandTotals[i] as number, m.dataType)}</span>
                      <span className="pbi-kpi-label">{m.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Grafik */}
              {viewMode !== "table" && chartData.length > 0 && (
                <div className="pbi-tile">
                  <div className="pbi-tile-head">
                    <span className="pbi-tile-title">Grafik — {result.measures[Math.min(chartMeasureIdx, result.measures.length - 1)]?.label}</span>
                    <span className="pbi-tile-subtitle">satır toplamları · ilk {chartData.length}</span>
                    <div className="pbi-chart-controls">
                      {result.measures.length > 1 && (
                        <select value={chartMeasureIdx} onChange={(e) => setChartMeasureIdx(Number(e.target.value))}>
                          {result.measures.map((m, i) => <option key={m.alias} value={i}>{m.label}</option>)}
                        </select>
                      )}
                      <div className="pbi-toggle-group pbi-chart-types">
                        <button className={chartType === "bar" ? "active" : ""} onClick={() => setChartType("bar")} title="Sütun">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
                        </button>
                        <button className={chartType === "line" ? "active" : ""} onClick={() => setChartType("line")} title="Çizgi">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 9 11 13 15 21 6" /></svg>
                        </button>
                        <button className={chartType === "pie" ? "active" : ""} onClick={() => setChartType("pie")} title="Pasta">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="pbi-tile-body">
                    <ResponsiveContainer width="100%" height={Math.max(340, chartData.length * 14)}>
                      {chartType === "pie" ? (
                        <PieChart>
                          <Pie data={chartData.slice(0, 12)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} innerRadius={55} paddingAngle={2}
                            label={({ name, percent }: any) => (percent > 0.05 ? `${name}: %${((percent || 0) * 100).toFixed(0)}` : "")}>
                            {chartData.slice(0, 12).map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                        </PieChart>
                      ) : chartType === "line" ? (
                        <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#5f6368" }} angle={-45} textAnchor="end" height={90} />
                          <YAxis tick={{ fontSize: 11, fill: "#5f6368" }} tickFormatter={(v) => formatNumber(v)} width={70} />
                          <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                          <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} />
                        </LineChart>
                      ) : (
                        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#5f6368" }} angle={-45} textAnchor="end" height={90} />
                          <YAxis tick={{ fontSize: 11, fill: "#5f6368" }} tickFormatter={(v) => formatNumber(v)} width={70} />
                          <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {chartData.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Pivot Tablo */}
              {viewMode !== "chart" && (
                <div className="pbi-tile">
                  <div className="pbi-tile-head">
                    <span className="pbi-tile-title">Pivot Tablo</span>
                    <span className="pbi-tile-subtitle">
                      {result.rowHeaders.length.toLocaleString("tr-TR")} satır
                      {hasCols ? ` × ${result.columnKeys.length} sütun` : ""} · {result.measures.length} ölçü
                    </span>
                  </div>
                  <div className="pbi-tile-body pbi-tile-table-body">
                    {result.rowHeaders.length === 0 ? (
                      <div className="pbi-tile-empty"><p>Filtrelerle eşleşen veri bulunamadı</p></div>
                    ) : (
                      <div className="pbi-table-wrapper">
                        <table className="pbi-table pbi-pivot">
                          <thead>
                            <tr>
                              {result.rowFields.map((f) => (
                                <th key={f} rowSpan={hasCols ? 2 : 1}>{label(f)}</th>
                              ))}
                              {hasCols ? (
                                <>
                                  {result.columnKeys.map((ck) => (
                                    <th key={ck} colSpan={result.measures.length} className="num pbi-pivot-colhead">{ck}</th>
                                  ))}
                                  <th colSpan={result.measures.length} className="num pbi-pivot-total">Genel Toplam</th>
                                </>
                              ) : (
                                result.measures.map((m) => <th key={m.alias} className="num">{m.label}</th>)
                              )}
                            </tr>
                            {hasCols && (
                              <tr>
                                {result.columnKeys.map((ck) => result.measures.map((m) => (
                                  <th key={ck + m.alias} className="num">{m.label}</th>
                                )))}
                                {result.measures.map((m) => (
                                  <th key={"gt" + m.alias} className="num pbi-pivot-total">{m.label}</th>
                                ))}
                              </tr>
                            )}
                          </thead>
                          <tbody>
                            {result.rowHeaders.map((hdr, r) => (
                              <tr key={r}>
                                {hdr.map((v, i) => <td key={i} className="pbi-pivot-rowhead">{v}</td>)}
                                {hasCols
                                  ? (
                                    <>
                                      {result.columnKeys.map((ck, c) => result.measures.map((m, mi) => (
                                        <td key={ck + m.alias} className="num">{fmtMeasure(result.cells[r][c][mi] as number, m.dataType)}</td>
                                      )))}
                                      {result.measures.map((m, mi) => (
                                        <td key={"rt" + m.alias} className="num pbi-pivot-total">{fmtMeasure(result.rowTotals[r][mi] as number, m.dataType)}</td>
                                      ))}
                                    </>
                                  )
                                  : result.measures.map((m, mi) => (
                                    <td key={m.alias} className="num">{fmtMeasure(result.rowTotals[r][mi] as number, m.dataType)}</td>
                                  ))}
                              </tr>
                            ))}
                            <tr className="pbi-pivot-grandrow">
                              <td className="pbi-pivot-rowhead" colSpan={result.rowFields.length}>Genel Toplam</td>
                              {hasCols
                                ? (
                                  <>
                                    {result.columnKeys.map((ck, c) => result.measures.map((m, mi) => (
                                      <td key={"ct" + ck + m.alias} className="num">{fmtMeasure(result.columnTotals[c][mi] as number, m.dataType)}</td>
                                    )))}
                                    {result.measures.map((m, mi) => (
                                      <td key={"g" + m.alias} className="num pbi-pivot-total">{fmtMeasure(result.grandTotals[mi] as number, m.dataType)}</td>
                                    ))}
                                  </>
                                )
                                : result.measures.map((m, mi) => (
                                  <td key={"g" + m.alias} className="num pbi-pivot-total">{fmtMeasure(result.grandTotals[mi] as number, m.dataType)}</td>
                                ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* ─── OVERLAYS ─── */}
      {showTemplates && (
        <div className="pbi-overlay" onClick={() => setShowTemplates(false)}>
          <div className="pbi-panel" onClick={(e) => e.stopPropagation()}>
            <div className="pbi-panel-head">
              <h2>Kayıtlı Şablonlar</h2>
              <button className="pbi-btn-close" onClick={() => setShowTemplates(false)}>✕</button>
            </div>
            <div className="pbi-panel-body">
              {templates.length === 0
                ? <div className="pbi-tile-empty"><p>Henüz kayıtlı pivot şablonu yok</p></div>
                : <div className="pbi-template-list">
                    {templates.map((t) => (
                      <div key={t.reportTemplateId} className="pbi-template-card">
                        <div className="pbi-template-info">
                          <h4>{t.name}</h4>
                          {t.description && <p>{t.description}</p>}
                          <div className="pbi-template-meta">
                            {t.isPublic && <span className="pbi-tag public">Herkese Açık</span>}
                            {t.createdByUserName && <span className="pbi-tag author">{t.createdByUserName}</span>}
                          </div>
                        </div>
                        <div className="pbi-template-actions">
                          <button className="btn btn-sm btn-primary" onClick={() => handleLoadTemplate(t, true)} title="Çalıştır">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={() => handleLoadTemplate(t, false)} title="Yükle (çalıştırma)">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /><path d="M4 19V5" /></svg>
                          </button>
                          <button className="btn btn-sm btn-soft-danger" onClick={() => handleDeleteTemplate(t)} title="Sil">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>}
            </div>
          </div>
        </div>
      )}

      {showSaveTemplate && (
        <div className="pbi-overlay" onClick={() => setShowSaveTemplate(false)}>
          <div className="pbi-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Şablon Olarak Kaydet</h3>
            <div className="pbi-modal-form">
              <div className="pbi-form-group">
                <label>Şablon Adı <span className="required">*</span></label>
                <input type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Örn: Firma × Maliyet Kategorisi" />
              </div>
              <div className="pbi-form-group">
                <label>Açıklama</label>
                <textarea value={templateDesc} onChange={(e) => setTemplateDesc(e.target.value)} placeholder="Rapor açıklaması" rows={2} />
              </div>
              <div className="pbi-form-group">
                <label className="pbi-checkbox-label">
                  <input type="checkbox" checked={templatePublic} onChange={(e) => setTemplatePublic(e.target.checked)} /> Herkese açık şablon
                </label>
              </div>
            </div>
            <div className="pbi-modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowSaveTemplate(false)}>İptal</button>
              <button className="btn btn-primary" onClick={handleSaveTemplate}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      <AlertModal isOpen={alertModal.isOpen} message={alertModal.message} type={alertModal.type}
        onClose={() => setAlertModal((p) => ({ ...p, isOpen: false }))} />
      <ConfirmModal isOpen={confirmModal.isOpen} message={confirmModal.message} type={confirmModal.type}
        onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal((p) => ({ ...p, isOpen: false }))} />
    </div>
  );
};

export default DynamicReports;
